import Button from "components/form/Button";
import PaymentMethodsComponent from "components/paymentMethod/PaymentMethodsComponent";
import { valuesProps } from "types/paymentMethods";
import { PaymentMethodList, SettingsValue } from "types/payments";
import CustomTooltip from "components/ui-component/CustomTooltip";
import WalletIcon from "icons/payments/WalletIcon";
import PaymentProcessStep2 from "pages/payments/PaymentProcessStep2";
import { useEffect } from "react";
import { useMediaQuery } from "react-responsive";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { updateOverlayState } from "store/slices/authSlice";
import { onSetShowDisclosure, useIsLogin } from "store/slices/commonSlice";
import { usePortalSettingsData } from "store/slices/dashboardSlice";
import {
  useBankData,
  useCardData,
  useHasLoadedPaymentMethods,
} from "store/slices/paymentMethodSlice";
import expiryDateValidation, {
  formatExpirationDate,
  getBankName,
  getCardType,
  validateCard,
} from "utils/HelperFunctions";
import { AddCircleIconBlue } from "utils/icon";
import { VerifyStripeCardPaymentParams } from "types/agreement";
import { RootState } from "store/store";
import { useDefaultPaymentMethodView } from "hooks/useDefaultPaymentMethodView";
import { PaymentMethodSelection } from "utils/updatePaymentPlanUtils";
import { isStripeKeyMissing } from "utils/stripePublishableKey";

interface PaymentComponentProps {
  currentStep: number;
  desktopStepMarker?: React.ReactNode;
  isRetryPayment: boolean;
  isAddNewCard: boolean;
  setIsAddNewCard: (val: boolean) => void;
  setDataPayment: any;
  dataPayment: any;
  setCurrentStep: (val: number) => void;
  completedSteps: [];
  setCompletedSteps: any;
  walletList: any;
  isDataLoading: boolean;
  setSavedCardsEmail: any;
  setShowPreview: (val: boolean) => void;
  paymentMethodList: PaymentMethodList[] | undefined;
  setSelectedMethod: (val: string) => void;
  cardNumber: string;
  setIconType: (val: string) => void;
  values: valuesProps;
  setValues: any;
  setIsRingLoading: (val: boolean) => void;
  setBankName: (val: string) => void;
  setBank: (val: string) => void;
  setErrMsg: (val: string | undefined) => void;
  iconType: string;
  setcardNumber: (val: string) => void;
  setIsDateErr: (val: boolean) => void;
  setnumberNotmatchedError: (val: boolean) => void;
  setSelectedState: (val: any) => void;
  selectedMethod: string;
  selectedState: any;
  isDateErr: boolean;
  numberNotmatchedError: boolean;
  bankName: string;
  isRingLoading: boolean;
  email: string;
  setEmail: (val: string) => void;
  data: any;
  setIsErr: (val: number) => void;
  setCardErrMsg: (val: string) => void;
  cardErrMsg: string;
  isErr: number;
  handleGooglePay: any;
  handlePrev: () => void;
  createPayPalIntent: any;
  isGoogleLoading: boolean;
  amtErr: string | null;
  setAmtErr: (val: string | null) => void;
  handleApplePay: any;
  isManualForm: boolean;
  setIsManualForm: (val: boolean) => void;
  BankOrCardComp?: any;
  queryParams: string;
  amount: any;
  showButtons?: boolean;
  isDisabled: boolean;
  isSecretLoading: boolean;
  cardRef?: any;
  cashAppRef?: any;
  bankRef?: any;
  amazonRef?: any;
  verifyStripeCardPayment?: ({
    intent,
    email,
    error,
    orderId,
  }: {
    intent?: string | null;
    email?: string;
    error?: string | null;
    orderId?: number;
  }) => Promise<void>;
  handlePlaidBankPayment?: (
    val: any,
    isCCTokenAvailable: boolean
  ) => Promise<void>;
  onPaymentMethodChange?: (selection: PaymentMethodSelection) => void;
}

const PaymentComponent = ({
  currentStep,
  desktopStepMarker,
  isRetryPayment,
  isAddNewCard,
  setIsAddNewCard,
  setDataPayment,
  dataPayment,
  setCurrentStep,
  walletList,
  isDataLoading,
  setSavedCardsEmail,
  setShowPreview,
  paymentMethodList,
  setSelectedMethod,
  cardNumber,
  setIconType,
  setValues,
  values,
  setIsRingLoading,
  setBankName,
  setBank,
  setErrMsg,
  iconType,
  setcardNumber,
  setIsDateErr,
  setnumberNotmatchedError,
  setSelectedState,
  selectedMethod,
  selectedState,
  isDateErr,
  numberNotmatchedError,
  bankName,
  isRingLoading,
  email,
  setEmail,
  data,
  setIsErr,
  setCardErrMsg,
  cardErrMsg,
  isErr,
  handleGooglePay,
  handlePrev,
  createPayPalIntent,
  isGoogleLoading,
  handleApplePay,
  setAmtErr,
  amtErr,
  isManualForm,
  setIsManualForm,
  BankOrCardComp = <></>,
  amount,
  queryParams,
  showButtons = false,
  setCompletedSteps,
  completedSteps,
  isDisabled,
  isSecretLoading,
  cardRef,
  cashAppRef,
  bankRef,
  amazonRef,
  verifyStripeCardPayment = ({
    intent,
    email,
    error,
    orderId,
  }: VerifyStripeCardPaymentParams): Promise<void> => {
    return Promise.resolve();
  },
  handlePlaidBankPayment = async (
    val: any,
    isCCTokenAvailable: boolean
  ): Promise<void> => {
    return Promise.resolve();
  },
  onPaymentMethodChange = () => {},
}: PaymentComponentProps) => {
  const isLogin = useIsLogin();
  const dispatch = useAppDispatch();
  const isLessThan769 = useMediaQuery({ query: "(max-width: 769px)" });
  const cardList = useCardData();
  const bankList = useBankData();
  const hasLoadedPaymentMethods = useHasLoadedPaymentMethods();
  const { paymentPermission } = useAppSelector(
    (state: RootState) => state.payment
  );
  const isCardBlocked =
    selectedMethod === "Card" && isStripeKeyMissing(paymentPermission, "CC");
  const currentPath = `${window.location.origin}${window.location.pathname}`;

  useEffect(() => {
    if (!cardNumber) {
      setIconType("");
      setValues({ ...values, card_type: "" });
      return;
    } else {
      setIconType(getCardType(cardNumber));
      setValues({ ...values, card_type: getCardType(cardNumber) });
    }
  }, [cardNumber]);

  const findBankByRoutingNumber = async (routingNumber: string) => {
    setIsRingLoading(true);
    try {
      let bankObj = await getBankName(routingNumber);
      if (bankObj?.type === "success") {
        setBankName(bankObj?.bank_name);
        setBank(bankObj?.bank_name);
        setValues((state: any) => ({
          ...state,
          bank_name: bankObj?.bank_name,
        }));
      } else {
        setErrMsg("Invalid routing number");
        setIsRingLoading(false);
      }
    } catch (error) {
      console.log("error =====", error);
      setErrMsg("Invalid routing number");
      setIsRingLoading(false);
    } finally {
      setIsRingLoading(false);
    }
  };

  const handleChangeCard = (e: any, name: string) => {
    onPaymentMethodChange({ method: selectedMethod, isNew: true });
    if (name === "x_card_num") {
      let icon = getCardType(e.target.value) || iconType;
      if (icon === "amex") {
        if (e.target.value.length < 18) {
          const cardNumber = e.target.value.replace(/\s/g, "");
          const formattedCardNumber = cardNumber
            .replace(/\D/g, "")
            .replace(/(\d{4})(\d{6})(\d{5})/, "$1 $2 $3");
          setcardNumber(formattedCardNumber);
          setValues({ ...values, [name]: e.target.value });
        }
      } else {
        if (e.target.value.length < 20) {
          const cardNumber = e.target.value.replace(/\s/g, "");
          const formattedCardNumber = cardNumber
            .replace(/\D/g, "")
            .replace(/(\d{4})(?=\d)/g, "$1 ");
          setcardNumber(formattedCardNumber);
          setValues({ ...values, [name]: e.target.value });
        }
      }
    } else if (name === "x_card_code") {
      if (iconType === "amex") {
        if (e.target.value.length < 5) {
          setValues({ ...values, [name]: e.target.value });
        }
      } else {
        if (e.target.value.length < 4) {
          setValues({ ...values, [name]: e.target.value });
        }
      }
    } else if (name === "x_exp_date") {
      const inputValue = e.target.value;
      setIsDateErr(false);
      if (inputValue?.length < 6) {
        const isValidDate: any = expiryDateValidation(inputValue);
        const formattedDate = formatExpirationDate(inputValue);
        setValues({ ...values, [name]: formattedDate });
        !isValidDate?.isValid && inputValue?.length === 5 && setIsDateErr(true);
      }
    } else if (name === "x_country") {
      setValues({ ...values, [name]: e?.value });
    } else if (name === "x_state") {
      setValues({ ...values, [name]: e?.name });
    } else {
      setValues({ ...values, [name]: e.target.value });
    }
  };

  const handleChangeBank = (e: any, name: string) => {
    onPaymentMethodChange({ method: selectedMethod, isNew: true });
    if (name === "routing_number") {
      if (e.target.value.length < 10) {
        setValues({ ...values, [name]: e.target.value });
        setBankName("");
        setBank("");
        setErrMsg(undefined);
        if (e.target.value.length === 9) {
          findBankByRoutingNumber(e.target.value);
        }
      }
    } else if (name === "r_account_number") {
      setnumberNotmatchedError(false);
      setValues({ ...values, [name]: e.target.value });
      if (values?.account_number?.length === e.target.value?.length) {
        setnumberNotmatchedError(values?.account_number !== e.target.value);
      }
    } else {
      setValues({ ...values, [name]: e.target.value });
    }
  };

  const { hasUsableSavedMethods } = useDefaultPaymentMethodView({
    cardList,
    bankList,
    walletList,
    paymentMethodList,
    paymentPermission,
    isLogin,
    hasLoadedPaymentMethods,
    setShowSavedMethods: setIsAddNewCard,
    setDataPayment,
    selectedMethod,
    setSelectedMethod,
  });

  return (
    <div
      className={`w-full lg:px-0 px-5 ${
        showButtons && isLessThan769 ? "pb-10" : ""
      } ${currentStep === 2 ? "lg:pb-[60px]" : ""}`}
    >
      <div className="w-full h-[2px] bg-bgSecondary mt-7 lg:block hidden"></div>
      <div
        data-update-step-heading="2"
        className="w-full relative flex lg:h-10 justify-between items-center max-w-[510px] pr-2.5"
      >
        {desktopStepMarker}
        <p
          className={`${
            currentStep === 2 ? "text-btnPrimaryActive" : "text-txtPrimary"
          } font-medium lg:text-xl text-lg `}
        >
          Payment method
        </p>
        {currentStep === 2 && (
          <div className="flex gap-3 justify-end items-center">
            {isAddNewCard ? (
              <p
                onClick={() => {
                  setIsAddNewCard(false);
                  onPaymentMethodChange({
                    method: selectedMethod,
                    isNew: true,
                  });
                }}
                className="flex gap-2 items-center cursor-pointer lg:text-sm text-xs font-medium text-blue hover:!text-btnPrimaryHover"
              >
                <AddCircleIconBlue /> Add New
              </p>
            ) : (
              hasUsableSavedMethods && (
                <>
                  {isLogin ? (
                    <span
                      className="xl:text-sm text-xs items-center text-blue cursor-pointer flex gap-2 hover:!text-btnPrimaryHover"
                      onClick={() => setIsAddNewCard(true)}
                    >
                      <span>
                        <WalletIcon
                          className="group"
                          _className="group-hover:!fill-btnPrimaryHover"
                        />
                      </span>
                      Select saved method
                    </span>
                  ) : (
                    <CustomTooltip
                      maxwidth="430px"
                      place="top"
                      id="saved-method"
                      content={`<div class="text-xs leading-[18px] font-normal">Log in to access your saved payment methods</div>`}
                    >
                      <span
                        id="saved-method"
                        className="xl:text-sm text-xs items-center text-blue cursor-pointer flex gap-2 underline hover:!text-btnPrimaryHover"
                        onClick={() => dispatch(updateOverlayState(true))}
                      >
                        <span>
                          <WalletIcon
                            className="group"
                            _className="group-hover:!fill-btnPrimaryHover"
                          />
                        </span>
                        Select saved method
                      </span>
                    </CustomTooltip>
                  )}
                </>
              )
            )}
          </div>
        )}
      </div>

      <div
        className={`max-w-[510px] w-full pb-5 ${
          !isLessThan769
            ? isRetryPayment
              ? "answerSection"
              : "answerSection1"
            : ""
        } ${currentStep === 2 ? "open" : ""} `}
      >
        {isAddNewCard ? (
          <>
            <div className="lg:pt-8 pt-0">
              <PaymentProcessStep2
                data={dataPayment}
                setData={setDataPayment}
                setCurrentStep={setCurrentStep}
                isDataLoading={isDataLoading}
                currentStep={currentStep}
                walletList={walletList}
                setSavedCardsEmail={setSavedCardsEmail}
                isPaymentLink={false}
                setShowPreview={setShowPreview}
                customClassName={showButtons ? "!mb-11" : ""}
                paymentMethodList={paymentMethodList}
                setSelectedMethod={setSelectedMethod}
                cppId={data?.clientCustomerData?.cpp_id}
                onPaymentMethodChange={onPaymentMethodChange}
              />
            </div>
          </>
        ) : (
          <div className="lg:pt-8 pt-0">
            <PaymentMethodsComponent
              cardData={{
                cardNumber,
                isDateErr,
                isErr,
                cardErrMsg,
                handleChangeCard,
                validateCard: () =>
                  validateCard({
                    values,
                    iconType,
                    setIsErr,
                    setCardErrMsg,
                    setErrMsg,
                  }),
                iconType,
                setIconType,
                setIsAddNewCardMakePayment: setIsAddNewCard,
                isAddNewCardMakePayment: isAddNewCard,

                showNewStripeCardForm: true,
              }}
              bankData={{
                bankName,
                numberNotmatchedError,
                setnumberNotmatchedError,
                handleChangeBank,
                selectedState,
                setSelectedState,
                stripeBankURL: `${currentPath}?method=bank${queryParams}`,
                isManualForm,
                setIsManualForm,
                handlePlaidBankPayment,
              }}
              paymentConfig={{
                paymentMethodList,
                selectedMethod,
                setSelectedMethod: (method: string) => {
                  setSelectedMethod(method);
                  onPaymentMethodChange({ method, isNew: true });
                },
                amount,
                isPaymentLink: false,
                isRecurring: true,
                isSchedulePayment: true,
                isUpdatePlan: true,
                isRetry: isRetryPayment,
                customClassName: showButtons ? "!mb-11" : "",
                dataArr: data?.paymentPlan,
              }}
              paymentHandlers={{
                handleGooglePay: (tokenData: any, amount: any) => {
                  handleGooglePay(tokenData, amount);
                },
                createPayPalIntent,
                handleApplePay,
                handleBack: handlePrev,
                setShowPreview,
                verifyStripeCardPayment,
              }}
              paymentUrls={{
                cashAppURL: `${currentPath}?method=cashApp${queryParams}`,
                stripeAmazonPayURL: `${currentPath}?method=amazonPay${queryParams}`,
              }}
              formState={{
                values,
                email,
                setEmail,
                errMsg: bankName,
                amtErr,
                setAmtErr,
                isRingLoading,
              }}
              paymentGatewayState={{
                isGoogleLoading,
              }}
              componentRefs={{
                bankRef,
                cardRef,
                amazonRef,
                cashAppRef,
              }}
              phoneAndAddressConfig={{}}
            />
            {!isCardBlocked && BankOrCardComp}
          </div>
        )}

        {currentStep === 2 && isRetryPayment && (
          <div>
            <div className="w-full pt-9">
              <div className=" bg-white w-full flex justify-between gap-5  ">
                <Button
                  variant="white"
                  text="Back"
                  className="lg:w-[150px] w-full"
                  onClick={handlePrev}
                />

                <Button
                  className="lg:w-[150px] w-full"
                  text="Next"
                  type="button"
                  isLoading={isSecretLoading}
                  isDisabled={isDisabled}
                  onClick={() => {
                    const tmp: number[] = Array.from(completedSteps);
                    tmp.push(2);
                    setCompletedSteps(tmp);
                    setCurrentStep(3);
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentComponent;
